(function () {
  function parseCapacity(value, title) {
    const fromMeta = String(value || '').match(/(\d+)/g);
    if (fromMeta && fromMeta.length) {
      return Math.max(...fromMeta.map(Number));
    }
    const fromTitle = String(title || '').match(/(\d+)\s*-\s*(\d+)/);
    if (fromTitle) return Number(fromTitle[2]);
    const single = String(title || '').match(/\b(\d+)\b/);
    return single ? Number(single[1]) : null;
  }

  function textBlob(product) {
    return [
      product.title,
      product.concept,
      product.style,
      product.subGroup,
      product.activity,
      product.level,
      ...(product.collections || []),
    ]
      .join(' ')
      .toLowerCase();
  }

  function isAccessory(product) {
    const blob = textBlob(product);
    return /spare|accessor|duffel|floor|footprint|pockit|shockcord|guy line|compression bag|rep kit|topphatt|netting|burner|peg|pole set|inner tent/.test(
      blob
    );
  }

  function scoreProduct(product, answers) {
    if (isAccessory(product)) return -100;

    let score = 0;
    const blob = textBlob(product);
    const concept = (product.concept || '').toLowerCase();
    const sub = (product.subGroup || '').toLowerCase();
    const style = (product.style || '').toLowerCase();
    const capacity = parseCapacity(product.persons, product.title);

    // Trip type maps to Omnium brav_sub_product_group + product_concept
    if (answers.trip === 'hiking') {
      if (/3 season/.test(sub) || /superlight|adventure|trek|scouter/.test(concept + ' ' + blob)) score += 38;
      if (/base camping|lavvo|varanger|pasvik|valhall/.test(blob)) score -= 22;
      if (/expedition|x-trem/.test(blob)) score -= 8;
    } else if (answers.trip === 'basecamp') {
      if (/base camping/.test(sub) || /challenger|seeker|lavvo|varanger|pasvik|valhall|gimle/.test(blob)) score += 40;
      if (/superlight/.test(concept) && !/base/.test(sub)) score -= 18;
    } else if (answers.trip === 'expedition') {
      if (/expedition/.test(sub) || /x-trem|expedition/.test(blob) || concept === 'x-trem') score += 45;
      if (/superlight/.test(concept) && !/x-trem|pro/.test(blob)) score -= 12;
      if (/base camping|lavvo/.test(blob) && !/x-trem|expedition/.test(blob)) score -= 6;
    }

    // Capacity bands from product_tent_persons + title ranges (4-6, 12-14, etc.)
    if (answers.capacity === '1-2') {
      if (capacity != null) {
        if (capacity <= 2) score += 34;
        else if (capacity === 3) score += 16;
        else score -= 20;
      } else if (/\b[12]\b|ringstind 2/.test(blob)) score += 18;
    } else if (answers.capacity === '3-4') {
      if (capacity != null) {
        if (capacity >= 3 && capacity <= 4) score += 34;
        else if (capacity === 2 || capacity === 5 || capacity === 6) score += 12;
        else if (capacity >= 10) score -= 24;
      } else if (/reinsfjell 3|fjellheimen 4|\b3\b|\b4\b/.test(blob)) score += 20;
    } else if (answers.capacity === '5+') {
      if (capacity != null) {
        if (capacity >= 6) score += 36;
        else if (capacity === 4 || capacity === 5) score += 10;
        else score -= 22;
      } else if (/4-6|6-8|8-10|10-12|12-14|valhall|varanger|pasvik/.test(blob)) score += 24;
    }

    // Season / conditions
    if (answers.season === '3-season') {
      if (/3 season/.test(sub) || /superlight|adventure|trek|scouter|seeker/.test(concept)) score += 28;
      if (/x-trem|expedition tents/.test(blob)) score -= 14;
    } else if (answers.season === 'winter') {
      if (/x-trem|expedition/.test(blob) || /expedition/.test(sub)) score += 36;
      if (/3 season/.test(sub) && !/x-trem|pro/.test(blob)) score -= 10;
    } else if (answers.season === 'mixed') {
      if (/adventure|trek|challenger|explorer|pro/.test(concept + ' ' + blob)) score += 20;
      if (/superlight/.test(concept)) score += 8;
    }

    // Space need maps to tent style + basecamp living volume
    if (answers.space === 'sleep') {
      if (/dome|tunnel/.test(style) || /superlight|adventure|trek/.test(concept)) score += 22;
      if (/lavvo/.test(style) || /base camping/.test(sub)) score -= 14;
    } else if (answers.space === 'gear') {
      if (/tunnel/.test(style) || /vestibule|camp|adventure|trek|seeker/.test(blob)) score += 24;
      if (/superlight/.test(concept) && !/camp/.test(blob)) score -= 4;
    } else if (answers.space === 'living') {
      if (/lavvo/.test(style) || /base camping|varanger|pasvik|valhall|dome 8|gimle/.test(blob)) score += 34;
      if (/superlight/.test(concept)) score -= 16;
    }

    return score;
  }

  function rank(catalog, answers) {
    return catalog
      .map((product) => ({ product, score: scoreProduct(product, answers) }))
      .filter((row) => row.score > 8)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((row) => row.product);
  }

  function buildBrowseUrl(baseUrl, answers) {
    const url = new URL(baseUrl, window.location.origin);
    const parts = [];
    if (answers.trip === 'hiking') parts.push('superlight');
    if (answers.trip === 'basecamp') parts.push('basecamp');
    if (answers.trip === 'expedition' || answers.season === 'winter') parts.push('x-trem');
    if (answers.capacity === '3-4') parts.push('3');
    if (answers.capacity === '5+') parts.push('6');
    if (parts.length) url.searchParams.set('q', parts.join(' '));
    return url.pathname + url.search;
  }

  class HelsportTentFinder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.catalog = JSON.parse(this.querySelector('[data-tent-catalog]')?.textContent || '[]');
      } catch (e) {
        this.catalog = [];
      }
      this.answers = {};
      this.stepIndex = 0;
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.progress = this.querySelector('[data-progress]');
      this.results = this.querySelector('[data-results]');
      this.browseLink = this.querySelector('[data-browse-link]');
      this.collectionUrl = this.dataset.collectionUrl || '/collections/telt';
      this.stage = this.closest('[data-finder-stage]');
      this.gate = this.stage?.querySelector('[data-finder-gate]');

      this.querySelectorAll('[data-choice]').forEach((btn) => {
        btn.addEventListener('click', () => this.onChoice(btn));
      });
      this.querySelectorAll('[data-back]').forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(this.stepIndex - 1));
      });
      this.querySelectorAll('[data-restart]').forEach((btn) => {
        btn.addEventListener('click', () => this.restart());
      });

      this.stage?.querySelectorAll('[data-finder-start]').forEach((btn) => {
        btn.addEventListener('click', () => this.open());
      });

      if (this.stage?.classList.contains('is-open') || this.stage?.dataset.autoOpen === 'true') {
        this.open();
      } else {
        this.goTo(0);
      }
    }

    open() {
      if (!this.stage) return;
      this.stage.classList.add('is-open');
      this.hidden = false;
      if (this.gate) this.gate.hidden = true;
      this.goTo(0);
      this.querySelector('[data-step]')?.querySelector('h2')?.focus?.();
    }

    close() {
      if (!this.stage) return;
      this.stage.classList.remove('is-open');
      this.hidden = true;
      if (this.gate) this.gate.hidden = false;
      this.restart();
    }

    onChoice(btn) {
      const key = btn.getAttribute('data-key');
      const value = btn.getAttribute('data-choice');
      if (!key) return;
      this.answers[key] = value;
      const step = btn.closest('[data-step]');
      step?.querySelectorAll('[data-choice]').forEach((el) => el.classList.toggle('is-selected', el === btn));
      window.setTimeout(() => {
        if (this.stepIndex >= this.steps.length - 2) this.finish();
        else this.goTo(this.stepIndex + 1);
      }, 180);
    }

    goTo(index) {
      const max = this.steps.length - 1;
      this.stepIndex = Math.max(0, Math.min(index, max));
      this.steps.forEach((step, i) => {
        step.hidden = i !== this.stepIndex;
      });
      if (this.progress) {
        const total = Math.max(1, this.steps.length - 1);
        const current = Math.min(this.stepIndex + 1, total);
        this.progress.style.setProperty('--hs-finder-progress', `${(current / total) * 100}%`);
        this.progress.setAttribute('aria-valuenow', String(current));
        this.progress.setAttribute('aria-valuemax', String(total));
      }
    }

    finish() {
      const picks = rank(this.catalog, this.answers);
      this.renderResults(picks);
      this.goTo(this.steps.length - 1);
      if (this.browseLink) {
        this.browseLink.href = buildBrowseUrl(this.collectionUrl, this.answers);
      }
    }

    renderResults(products) {
      if (!this.results) return;
      if (!products.length) {
        this.results.innerHTML = `<p class="hs-finder__empty">${this.dataset.emptyText || 'No clear match yet. Browse all tents.'}</p>`;
        return;
      }
      this.results.innerHTML = products
        .map((product, i) => {
          const price = product.price || '';
          const concept = product.concept ? `<span class="hs-finder__chip">${product.concept}</span>` : '';
          const capacity = parseCapacity(product.persons, product.title);
          const capacityLabel = capacity ? `<span class="hs-finder__chip">${capacity}p</span>` : '';
          const img = product.image
            ? `<img src="${product.image}" alt="" width="640" height="640" loading="lazy">`
            : `<div class="hs-finder__ph"></div>`;
          return `<a class="hs-finder__card" href="${product.url}" style="--i:${i}">
            <div class="hs-finder__thumb">${img}</div>
            <div class="hs-finder__meta">
              <span class="hs-finder__rank">0${i + 1}</span>
              <div class="hs-finder__chips">${concept}${capacityLabel}</div>
              <h3>${product.title}</h3>
              ${price ? `<p class="hs-finder__price">${price}</p>` : ''}
              <span class="hs-finder__cta">${this.dataset.viewText || 'View tent'}</span>
            </div>
          </a>`;
        })
        .join('');
    }

    restart() {
      this.answers = {};
      this.querySelectorAll('[data-choice].is-selected').forEach((el) => el.classList.remove('is-selected'));
      if (this.results) this.results.innerHTML = '';
      this.goTo(0);
    }
  }

  if (!customElements.get('helsport-tent-finder')) {
    customElements.define('helsport-tent-finder', HelsportTentFinder);
  }
})();
